import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { updateChannelMember } from 'flow/actions/channels-actions';
import { updateProfile } from 'flow/actions/profile-actions';
import useProfiles from 'flow/hooks/useProfiles';
import { ChannelKey, ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { ChannelLoginList, Profile } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { generatePath } from 'flow/utils/router-utils';
import { fromJS, List } from 'immutable';
import { useHistory, useParams } from 'react-router-dom';
import Toggle from '../ui-components/Toggle';
import Page from './Page';
import SettingRow from './SettingRow';
import {
    selectChatUserKey,
    selectExtensionActiveProfileKey,
} from 'flow/selectors/ExtensionSelectors';
import {
    selectChannelExists,
    selectChannelMembers,
    selectChannelName,
} from 'flow/selectors/channel-selectors';

const SettingsChannelLoginProfile = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey, channelKey } = useParams<{
        extensionKey: ExtensionKey;
        channelKey: ChannelKey;
    }>();
    const { profiles, getProfile } = useProfiles(extensionKey);

    const channelExists = useAppSelector((state) =>
        selectChannelExists(state, channelKey),
    );

    const channelName = useAppSelector((state) =>
        selectChannelName(state, channelKey),
    );

    const channelMembers = useAppSelector((state) =>
        selectChannelMembers(state, channelKey),
    );

    const chatUserKey = useAppSelector((state) =>
        selectChatUserKey(state, extensionKey),
    );

    const extensionActiveProfileKey = useAppSelector((state) =>
        selectExtensionActiveProfileKey(state, extensionKey),
    );

    if (!channelExists) {
        return null;
    }

    const getLoggedInValue = (
        profile: TypedMap<Profile>,
        channelKey: string,
    ) => {
        const channelLogin = profile
            .get('channelLoginList')
            .find(
                (channelLogin) =>
                    channelLogin.get('channelEntityKey') === channelKey,
            );
        if (!channelLogin) {
            return false;
        }
        return channelLogin.get('loggedIn', false);
    };

    const toggledProfileKeys = profiles
        .filter((profile) => getLoggedInValue(profile, channelKey))
        .map((profile) => profile.get('key'));

    const updateOrCreateLoginForChannel = (
        channelLoginList: List<TypedMap<ChannelLoginList>>,
        channelKey: ChannelKey,
        newValue: boolean,
    ) => {
        const indexOfChannelLoginToUpdate = channelLoginList.findIndex(
            (channelLogin: TypedMap<ChannelLoginList>) =>
                channelLogin.get('channelEntityKey') === channelKey,
        );
        if (indexOfChannelLoginToUpdate === -1) {
            return channelLoginList.push({
                loggedIn: newValue,
                channelEntityKey: channelKey,
            });
        }
        return channelLoginList.setIn(
            [indexOfChannelLoginToUpdate, 'loggedIn'],
            newValue,
        );
    };

    const handleProfileToggle = (profileKey: ProfileKey, newValue: boolean) => {
        const profileDTO = getProfile(profileKey);
        const channelLoginList = profileDTO.get('channelLoginList', List());
        const updatedChannelLoginList = updateOrCreateLoginForChannel(
            channelLoginList,
            channelKey,
            newValue,
        );

        const updatedProfileDTO = fromJS({
            key: profileKey,
            channelLoginList: updatedChannelLoginList,
        });

        dispatch(updateProfile(extensionKey, updatedProfileDTO));

        if (extensionActiveProfileKey === profileKey) {
            const memberDTO = channelMembers.find(
                (member) => member.get('chatUserKey') === chatUserKey,
            );
            const updatedMemberDTO = memberDTO.set('loggedIn', newValue);
            dispatch(
                updateChannelMember(
                    channelKey,
                    updatedMemberDTO.get('key'),
                    updatedMemberDTO,
                ),
            );
        }
    };

    return (
        <Page
            header={channelName}
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.queue-and-chat-login'], {
                        extensionKey,
                    }),
                )
            }
        >
            {profiles?.map((profile) => (
                <SettingRow
                    className="hover:bg-transparent"
                    key={`${profile.get('key')}-toggle`}
                    cta={
                        <Toggle
                            value={toggledProfileKeys.includes(
                                profile.get('key'),
                            )}
                            onChange={(value) =>
                                handleProfileToggle(profile.get('key'), value)
                            }
                            text={profile.get('description')}
                        />
                    }
                >
                    {profile.get('description')}
                </SettingRow>
            ))}
        </Page>
    );
};

export default SettingsChannelLoginProfile;
